import { getSession } from './authService';
import { getNetlifyFunctionUrl } from '../config/config';

/**
 * Helper function to get auth token from session
 */
const getAuthToken = async () => {
  const { session, error } = await getSession();
  if (error || !session) {
    throw new Error('No valid session found');
  }
  return session.access_token;
};

/**
 * Folio Service - Handles all folio management operations
 */
class FolioService {
  /**
   * Get current folio configuration
   */
  async getFolioConfig() {
    try {
      const token = await getAuthToken();
      
      const response = await fetch(getNetlifyFunctionUrl('folio-management'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'getFolioConfig'
        })
      });

      // Check if response is HTML (404 or other error page)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        throw new Error('La función de gestión de folios no está disponible. Por favor, ejecute la migración de base de datos primero.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to get folio configuration');
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error getting folio config:', error);
      throw error;
    }
  }

  /**
   * Update folio configuration
   * @param {number} ultimoFolio - The new ultimo folio value
   */
  async updateFolioConfig(ultimoFolio) {
    try {
      const token = await getAuthToken();
      
      const response = await fetch(getNetlifyFunctionUrl('folio-management'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'updateFolioConfig',
          ultimoFolio: ultimoFolio
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update folio configuration');
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error updating folio config:', error);
      throw error;
    }
  }

  /**
   * Assign next folio to a preparativo
   * @param {number} preparativoId - The preparativo ID to assign folio to
   */
  async assignNextFolio(preparativoId) {
    try {
      const token = await getAuthToken();
      
      const response = await fetch(getNetlifyFunctionUrl('folio-management'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'assignNextFolio',
          preparativoId: preparativoId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to assign folio');
      }

      const data = await response.json();
      return data.folio;
    } catch (error) {
      console.error('Error assigning next folio:', error);
      throw error;
    }
  }

  /**
   * Generate instructivo with folio
   * @param {number} preparativoId - The preparativo ID
   * @param {number} folio - The folio number to use
   */
  async generateInstructivoWithFolio(preparativoId, folio) {
    try {
      const token = await getAuthToken();
      
      const response = await fetch(getNetlifyFunctionUrl('generar-instructivo-embarque'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          preparativoId: preparativoId,
          folio: folio
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate instructivo');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error generating instructivo with folio:', error);
      throw error;
    }
  }

  /**
   * Get folio for a specific preparativo from database
   * @param {number} preparativoId - The preparativo ID
   */
  async getPreparativoFolio(preparativoId) {
    try {
      const token = await getAuthToken();
      
      // Use existing API to get preparativo DUS data
      const response = await fetch(getNetlifyFunctionUrl('preparativos-dus'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'get',
          preparativoId: preparativoId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get preparativo folio');
      }

      const data = await response.json();
      return data.data?.folio_instructivo || null;
    } catch (error) {
      console.error('Error getting preparativo folio:', error);
      throw error;
    }
  }
}

// Export singleton instance
const folioService = new FolioService();
export default folioService;
